import {
  Box,
  Button,
  Typography,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { download, getSignedUrl } from 'utils/aws/s3';
import { downloadPdfFromBase64, getFileType } from 'utils/files';
import { LoadingScreen } from '../Loaders';
import ModalFileViewer from './ModalFileViewer';
import DocumentViewer from './Types/DocumentViewer';
import ImageViewer from './Types/ImageViewer';
import OtherViewer from './Types/OtherViewer';
import VideoViewer from './Types/VideoViewer';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import DocumentActionsBar from './components/DocumentActionsBar';
import DocumentThumbnailsSidebar from './components/DocumentThumbnailsSidebar';

const FileViewer = ({ data, actions, thumbs, modal, onClose, ownModal }) => {
  const [fileState, setFileState] = useState({
    type: 'other',
    subType: 'unknown',
    isLoading: true,
    contentUrl: '',
    fileName: '',
    error: null,
  });
  const contentRef = useRef(null);
  const [pdfConfig, setPdfConfig] = useState({
    width: typeof modal === 'boolean' || ownModal ? 600 : 1000,
    numPages: 0,
    pageNumber: 1,
    isFullWidth: false,
  });
  const [rotation, setRotation] = useState(0);
  const [inputValue, setInputValue] = useState(pdfConfig.pageNumber.toString());
  const isMounted = useRef(true);


  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const loadData = async () => {
    setFileState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));
    try {
      let fileData;

      if (typeof data === 'string') {
        fileData = Array.isArray(data)
          ? data.find((item) => item.active) || data[0]
          : data;
      } else {
        fileData = Array.isArray(data)
          ? data.find((item) => item.active) || data[0]
          : data?.data?.url;
      }

      if (!fileData) return;

      let fileName;
      let contentUrl = '';
      let type = {
        type: 'other',
        subType: 'unknown',
      };

      if (fileData?.Content) {
        contentUrl = `data:${fileData?.ContentType || 'application/pdf'};base64,${fileData.Content}`;
        fileName = 'Documento PDF';
        type = getFileType('application/pdf', true);
      } else if (fileData?.data?.url) {
        fileName = fileData?.data?.fileName || String(fileData.data.url).split('/').pop() || 'file';
        contentUrl = await getSignedUrl(fileData.data, 1000);
        type = getFileType(fileData.data);
      } else if (typeof fileData === 'string' && fileData.startsWith('data:application/pdf')) {
        contentUrl = fileData;
        fileName = 'Documento PDF';
        type = getFileType('application/pdf', true);
      } else {
        fileName = fileData?.fileName || String(fileData).split('/').pop() || 'file';
        contentUrl = await getSignedUrl(data?.data?.custom ? data?.data : fileData, 1000);
        type = getFileType(fileData);
      }

      if (isMounted.current) {
        setFileState({
          type: type.type,
          subType: type.subType,
          isLoading: false,
          contentUrl,
          fileName,
          error: null,
        });
      }
    } catch (error) {
      console.error('Error loading file:', error);
      if (isMounted.current) {
        setFileState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Error al cargar el archivo',
        }));
      }
    }
  };

  const handleWidthSize = (action) => {
    setPdfConfig((prev) => {
      let newWidth = prev.width;
      if (action === 'zoomIn') newWidth = Math.min(prev.width + 100, 1500);
      if (action === 'zoomOut') newWidth = Math.max(prev.width - 100, 300);
      if (action === 'reset') newWidth = 600;
      if (action === 'fitToWidth' && contentRef.current) newWidth = prev.isFullWidth ? 600 : contentRef?.current?.clientWidth - 40;
      return {
        ...prev,
        width: newWidth,
        isFullWidth: action === 'fitToWidth' ? !prev.isFullWidth : false,
      };
    });
  };

  const handleRotate = (plus = true) => {
    setRotation((prev) => plus ? prev + 90 : prev - 90);
  };

  const downloadDocument = async () => {
    try {
      if (typeof fileState.contentUrl === 'string' && fileState.contentUrl.includes('base64')) {
        downloadPdfFromBase64(fileState.contentUrl.split(',')[1], fileState.fileName);
      }
      else if (fileState.contentUrl.includes('base64')) {
        downloadPdfFromBase64(data.Content, fileState.fileName);
      }
      else if (fileState.contentUrl && typeof fileState.contentUrl === 'string' &&
        fileState.contentUrl.includes('amazonaws.com')) {
        if (fileState.contentUrl.includes('Signature=')) {
          window.open(fileState.contentUrl);
        } else {
          const signedUrl = await getSignedUrl(
            data?.data?.url || data?.url || fileState.contentUrl,
            120,
          );
          window.open(signedUrl);
        }
      }
      else {
        download(data);
      }
    } catch (err) {
      console.error('Error al descargar:', err);
    }
  };

  useEffect(() => { if (data) loadData(); }, [data, modal]);
  useEffect(() => {
    if (fileState.error) setFileState((prev) => ({
      ...prev,
      contentUrl: '',
      fileName: 'Error',
    }));
  }, [fileState.error]);

  useEffect(() => {
    if (!pdfConfig.isFullWidth || !contentRef.current) return;

    let timeoutId = null;

    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setPdfConfig((prev) => ({
          ...prev,
          width: contentRef?.current?.clientWidth - 40,
        }));
      }, 100);
    });
    resizeObserver.observe(contentRef.current);
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [pdfConfig.isFullWidth]);

  useEffect(() => {
    setInputValue(pdfConfig.pageNumber.toString());
  }, [pdfConfig.pageNumber]);

  const content = fileState.error ? (
    <Box textAlign="center" p={4} style={{
      width: '100%',
    }}>
      <Typography color="error" gutterBottom>{fileState.error}</Typography>
      <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={loadData}>Reintentar</Button>
    </Box>
  ) : (
    <Box ref={contentRef} style={{
      display: 'grid',
      placeItems: 'center',
      height: '100%',
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {(fileState.isLoading || !data) && <Box style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
      }}>
        <LoadingScreen />
      </Box>}
      {fileState.type === 'image' && <ImageViewer contentUrl={fileState.contentUrl} pdfWidth={pdfConfig.width} rotation={rotation} setFileState={setFileState} />}
      {fileState.type === 'document' && <DocumentViewer
        pdfConfig={pdfConfig}
        modal={ownModal || false}
        setPdfConfig={setPdfConfig}
        url={fileState.contentUrl}
        subType={fileState.subType}
        fileName={fileState.fileName}
        setError={(e) => setFileState((prev) => ({
          ...prev,
          error: e,
        }))} />}
      {fileState.type === 'video' && <VideoViewer contentUrl={fileState.contentUrl} fileName={fileState.fileName} />}
      {fileState.type === 'other' && <OtherViewer contentUrl={fileState.contentUrl} fileName={fileState.fileName} />}
    </Box>
  );

  return (
    <Box style={{
      position: 'relative',
    }}>
      {typeof modal === 'boolean' ? (
        <ModalFileViewer
          open={modal}
          onClose={() => {
            setFileState({
              ...fileState,
              contentUrl: '',
            }); onClose();
          }}
          title={fileState.fileName}
          actions={actions}
          thumbs={thumbs}
          handleWidthSize={handleWidthSize}
          handleRotate={handleRotate}
          zoomSize={pdfConfig.width}
          onDownload={downloadDocument}
          contentUrl={fileState.contentUrl}
          inputValue={inputValue}
          setInputValue={setInputValue}
          pdfConfig={{
            ...pdfConfig,
            is: fileState.type === 'document',
            type: fileState.type,
            subType: fileState.subType,
            contentUrl: fileState.contentUrl,
          }}
          setPdfConfig={setPdfConfig}
        >
          {content}
        </ModalFileViewer>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}>
          {actions && (
            <DocumentActionsBar
              pdfConfig={{
                ...pdfConfig,
                is: fileState.type === 'document',
                type: fileState.type,
                subType: fileState.subType,
                contentUrl: fileState.contentUrl,
              }}
              setPdfConfig={setPdfConfig}
              onRotateLeft={() => handleRotate(false)}
              onRotateRight={() => handleRotate(true)}
              handleWidthSize={handleWidthSize}
              handleRotate={handleRotate}
              zoomSize={pdfConfig.width}
              onDownload={downloadDocument}
              inputValue={inputValue}
              setInputValue={setInputValue}
            />
          )}
          <Box style={{
            display: 'flex',
            flex: 1,
            width: '100%',
            minHeight: 0,
          }}>
            {thumbs && fileState.subType !== 'word' && fileState.type === 'document' && pdfConfig.numPages !== 1 && (
              <Box sx={{
                overflowY: 'auto',
                height: 'inherit',
                maxHeight: '75dvh',
                borderRight: '1px solid #e0e0e0',
                position: 'sticky',
                top: actions ? 20 : 0,
              }}>
                <DocumentThumbnailsSidebar
                  pdfConfig={{
                    ...pdfConfig,
                    is: fileState.type === 'document',
                    type: fileState.type,
                    subType: fileState.subType,
                    contentUrl: fileState.contentUrl,
                  }}
                  setPdfConfig={setPdfConfig}
                  containerRef={contentRef}
                />
              </Box>
            )}
            <Box style={{
              display: 'grid',
              placeItems: 'center',
              width: '100%',
              height: '100%',
              flex: 1,
            }}>
              {content}
            </Box>

          </Box>
        </Box>
      )}
    </Box>
  );
};

FileViewer.propTypes = {
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  actions: PropTypes.bool,
  thumbs: PropTypes.bool,
  modal: PropTypes.bool,
  onClose: PropTypes.func,
  ownModal: PropTypes.bool,
};

export default FileViewer;
